// DASHBOARD TEMPLATES — named layouts (which nav sections/items a tenant sees) so we
// configure a tenant by picking a template instead of hand-removing menu items.
//
// A tenant selects one via features.template = '<key>'. DashboardLayout resolves the
// template → its sections. The super-admin Templates page renders this registry so the
// owner can see what each template entails. Templates are DATA — edit here to change a
// layout everywhere it's used.
//
// Back-compat: existing flags map to templates — features.executiveAdmin →
// 'executive-assistant', features.dashboard === 'tasks' → 'small-business-owner'.

import {
  LayoutDashboard, Phone, PhoneIncoming, PhoneOutgoing, DollarSign, Users, Sparkles,
  Settings as SettingsIcon, HelpCircle, ListChecks, Contact as ContactIcon,
  Video, GraduationCap, Truck, Building2, PhoneOff, FileText, ClipboardCheck, Flame,
  PhoneCall, UserCheck
} from 'lucide-react';

// ── Reusable nav items (single source so labels/routes stay consistent) ──────
const NAV = {
  overview: { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  calls: { to: '/calls', label: 'Calls', icon: Phone },
  tasks: { to: '/tasks', label: 'Tasks', icon: ListChecks },
  contacts: { to: '/contacts', label: 'Contacts', icon: ContactIcon },
  conference: { to: '/conference-calls', label: 'Conference Calls', icon: Video },
  expert: { to: '/expert-mode', label: 'Expert Mode', icon: GraduationCap },
  // Labeled "Call Knowledge" — the page is mostly the AI's answer material
  // (Business Info, FAQ, Knowledge, Notes) plus call handling. Route path stays
  // /call-routing so existing links/bookmarks don't break.
  callRouting: { to: '/call-routing', label: 'Call Knowledge', icon: PhoneIncoming },
  sales: { to: '/sales', label: 'Sales', icon: DollarSign },
  leads: { to: '/leads', label: 'Leads', icon: Flame },
  // Shared pitch bundles (body + behaviour + DTMF + opener). Campaign-facing,
  // so it sits with the sales tooling rather than in the owner Admin block.
  pitches: { to: '/pitches', label: 'Pitches', icon: FileText },
  // The rep's own handset: their microphone published into the LiveKit room a call
  // is running in, so a human — not the AI — talks to the prospect. Sits with the
  // sales tooling because it is only meaningful on a line that dials out.
  //
  // ⛔ SUPER-ADMIN ONLY FOR NOW. Every call from it reaches a real business, and the
  // server gates it on REP_SOFTPHONE_ENABLED + owner regardless of who can see the
  // link — but there is no reason to show a tenant a page the server will refuse.
  repPhone: { to: '/rep-phone', label: 'Rep Phone', icon: PhoneCall },
  // Will Engine's paying subscribers. Read-only, and read THROUGH core — the list
  // lives in Will Engine's database, not this one.
  subscribers: { to: '/subscribers', label: 'Subscribers', icon: UserCheck },
  employee: { to: '/employee', label: 'Employee', icon: Users },
  settings: { to: '/settings', label: 'Settings', icon: SettingsIcon },
  demonstrations: { to: '/demonstrations', label: 'Demonstrations', icon: Sparkles },
  help: { to: '/help', label: 'Help', icon: HelpCircle },
  freightBroker: { to: '/freight-broker', label: 'Freight Broker', icon: Truck },
  // The care line's SCHEDULED calls — the times Alfred rings out, what happened at
  // each one today, and who gets told when nobody could be reached. Deliberately a
  // top-level nav item rather than a card buried in Settings: on a care line the
  // operator's daily question is "did the check happen", which is not a setting.
  outboundCalls: { to: '/outbound-calls', label: 'Outbound Calls', icon: PhoneOutgoing },
  // Trucking carrier surfaces — full-bleed pages with their own chrome.
  truckingDispatch: { to: '/trucking/dispatch', label: 'Dispatch Board', icon: Truck },
  truckingSetup: { to: '/trucking/setup', label: 'Carrier Setup', icon: ClipboardCheck }
};

// The owner-only Admin section (platform tooling) — appended for the super-admin
// template only.
export const OWNER_SECTION = {
  label: 'Admin',
  items: [
    { to: '/customers', label: 'Customers', icon: Building2 },
    { to: '/freight-broker', label: 'Freight Broker', icon: Truck },
    { to: '/pricing', label: 'Pricing & Costs', icon: DollarSign },
    { to: '/templates', label: 'Templates', icon: LayoutDashboard },
    { to: '/demo-callers', label: 'Retired Demos', icon: PhoneOff }
  ]
};

// ── Templates ────────────────────────────────────────────────────────────────
export const TEMPLATES = {
  'super-admin': {
    label: 'Super Admin',
    description: 'Full platform access — every section plus the owner Admin tools. This is the operator dashboard.',
    includesOwnerAdmin: true,
    sections: [
      // ⛔ THE PAGE EXISTED AND NOTHING LINKED TO IT. /outbound-calls has been built, routed
      // and admin-gated all along — it carries the "Call now" form AND the record of calls
      // Alfred placed. It was only ever listed on the care-line template, so on this one it
      // was unreachable, and the only way to make an outbound call was to build a sales
      // campaign and throw it away afterwards. Brian, 2026-09-24: "there's not an outbound
      // calls section, which there should be." There was; it just had no door.
      { label: 'Workspace', items: [NAV.overview, NAV.calls, NAV.outboundCalls] },
      { label: 'Business', items: [NAV.callRouting, NAV.sales, NAV.leads, NAV.pitches, NAV.repPhone, NAV.employee] },
      { label: 'Account', items: [NAV.settings, NAV.demonstrations, NAV.help] }
    ]
  },
  'executive-assistant': {
    label: 'Executive Assistant',
    description: 'Personal AI chief-of-staff (e.g. 414 / 530). Task-first: tasks, contacts, conference calls, expert mode. No sales/campaign tooling.',
    sections: [
      { label: 'Assistant', items: [NAV.overview, NAV.tasks, NAV.contacts, NAV.conference, NAV.expert] },
      { label: 'Account', items: [NAV.settings, NAV.help] }
    ]
  },
  'small-business-owner': {
    label: 'Small Business Owner',
    description: 'A trimmed operator view with a task area — calls + call routing + tasks, without the Alfred-specific Sales/Demos/Admin sections.',
    sections: [
      { label: 'Workspace', items: [NAV.overview, NAV.tasks, NAV.calls] },
      { label: 'Business', items: [NAV.callRouting, NAV.employee] },
      { label: 'Account', items: [NAV.settings, NAV.help] }
    ]
  },
  'family-care': {
    label: 'Family Care Line',
    description: 'A private well-being line for one person (e.g. Gary\'s Knoxville line). Scheduled outbound check-in calls, the people Alfred can reach, and what he knows — with none of the sales, campaign, demo or platform tooling, which does not belong on a family account.',
    sections: [
      { label: 'Workspace', items: [NAV.overview, NAV.calls] },
      { label: 'Care line', items: [NAV.outboundCalls, NAV.contacts, NAV.callRouting] },
      { label: 'Account', items: [NAV.settings, NAV.help] }
    ]
  },
  'freight-broker': {
    label: 'Freight Broker',
    description: 'For a freight brokerage customer — calls + broker tooling. (Draft — refine as the first broker customer onboards.)',
    sections: [
      { label: 'Workspace', items: [NAV.overview, NAV.calls] },
      { label: 'Freight', items: [NAV.freightBroker, NAV.callRouting] },
      { label: 'Account', items: [NAV.settings, NAV.help] }
    ]
  },
  'transportation-company': {
    label: 'Transportation Company',
    description: 'For a carrier running their own trucks — the dispatch board (loads, drivers, the handoff queue) and the carrier setup walkthrough, alongside the real call record. NOTE: the two trucking screens are still prototypes on sample data and label themselves as such; the rest of the sections are live.',
    sections: [
      { label: 'Workspace', items: [NAV.overview, NAV.calls] },
      { label: 'Trucking', items: [NAV.truckingDispatch, NAV.truckingSetup] },
      { label: 'Account', items: [NAV.settings, NAV.help] }
    ]
  },
  'will-engine': {
    label: 'Will Engine',
    description: 'The consumer subscription line (the Coach and the Executive Assistant). Subscribers, their packages, the calls Alfred places to them, and what he knows — with none of the campaign, lead, pitch or demo tooling, which belongs to the platform\'s own sales rather than to a product with paying subscribers.',
    // ⛔ OWNER ADMIN STAYS. The nav below is trimmed to what this TENANT uses, but the
    // Admin block is platform tooling that belongs to the person, not the tenant —
    // dropping it would strand the owner in a tenant with no way back to Customers or
    // Pricing without switching first.
    includesOwnerAdmin: true,
    sections: [
      { label: 'Workspace', items: [NAV.overview, NAV.calls] },
      { label: 'Subscribers', items: [NAV.subscribers, NAV.callRouting] },
      { label: 'Account', items: [NAV.settings, NAV.help] }
    ]
  },
  standard: {
    label: 'Standard',
    description: 'The default tenant layout — Overview, Calls, the three business pillars, and account settings.',
    sections: [
      { label: 'Workspace', items: [NAV.overview, NAV.calls] },
      { label: 'Business', items: [NAV.callRouting, NAV.sales, NAV.leads, NAV.employee] },
      { label: 'Account', items: [NAV.settings, NAV.demonstrations, NAV.help] }
    ]
  }
};

// The order shown on the Templates page.
export const TEMPLATE_ORDER = [
  'super-admin', 'executive-assistant', 'small-business-owner', 'family-care',
  'freight-broker', 'transportation-company', 'will-engine', 'standard'
];

// Resolve which template a viewer gets, honoring explicit features.template, then
// the legacy flags, then owner → super-admin, else standard.
export function resolveTemplateKey(features = {}, isOwner = false) {
  if (features?.template && TEMPLATES[features.template]) return features.template;
  if (features?.executiveAdmin) return 'executive-assistant';
  if (features?.dashboard === 'tasks') return 'small-business-owner';
  if (isOwner) return 'super-admin';
  return 'standard';
}

export function getTemplate(key) {
  return TEMPLATES[key] || TEMPLATES.standard;
}
